import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Monitor, 
  Users, 
  Target, 
  Award, 
  Globe, 
  Shield,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import { getCanonicalUrl } from '../utils/config';

const AboutPage = () => {
  return (
    <>
      <Helmet>
        <title>About Us - URL Monitor Pro | Professional Website Monitoring Service</title>
        <meta name="description" content="Learn about URL Monitor Pro - a professional website monitoring service dedicated to keeping your websites online 24/7 with instant alerts and detailed analytics." />
        <meta name="keywords" content="about URL Monitor Pro, website monitoring company, uptime monitoring service, website monitoring team" />
        <link rel="canonical" href={getCanonicalUrl('/about')} />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center">
                <Monitor className="w-8 h-8 text-blue-600 mr-3" />
                <span className="text-xl font-bold text-gray-900">URL Monitor Pro</span>
              </Link>
              <div className="hidden md:flex items-center space-x-8">
                <a href="/#features" className="text-gray-600 hover:text-gray-900 transition-colors">Features</a>
                <Link to="/pricing" className="text-gray-600 hover:text-gray-900 transition-colors">Pricing</Link>
                <Link to="/about" className="text-blue-600 font-semibold">About</Link>
                <Link to="/contact" className="text-gray-600 hover:text-gray-900 transition-colors">Contact</Link>
                <Link to="/login" className="text-gray-600 hover:text-gray-900 transition-colors">Login</Link>
                <Link to="/register" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                  Start Free
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                About URL Monitor Pro
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We're on a mission to help businesses maintain perfect website uptime with professional monitoring tools, instant alerts, and actionable insights.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Our Mission</h3>
                <p className="text-gray-600">
                  To provide the most reliable and user-friendly website monitoring service that helps businesses prevent downtime and maintain customer trust.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Our Team</h3>
                <p className="text-gray-600">
                  A dedicated team of developers, DevOps engineers, and customer success specialists passionate about website reliability.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Our Values</h3>
                <p className="text-gray-600">
                  Reliability, transparency, and customer success drive everything we do. We believe in simple, honest pricing and exceptional service.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                URL Monitor Pro was born from a simple frustration: existing website monitoring tools were either too complex, too expensive, or simply unreliable when you needed them most.
              </p>

              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                As developers and business owners ourselves, we experienced firsthand the pain of discovering website downtime from angry customers rather than proactive alerts. We knew there had to be a better way.
              </p>

              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                That's why we built URL Monitor Pro with three core principles:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 my-12">
                <div className="text-center p-6 bg-blue-50 rounded-lg">
                  <Shield className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Reliability First</h3>
                  <p className="text-gray-600">Our monitoring infrastructure is built for 99.9% uptime with redundancy across multiple global locations.</p>
                </div>

                <div className="text-center p-6 bg-green-50 rounded-lg">
                  <Users className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">User-Friendly</h3>
                  <p className="text-gray-600">Complex monitoring shouldn't require complex tools. We make professional monitoring accessible to everyone.</p>
                </div>

                <div className="text-center p-6 bg-purple-50 rounded-lg">
                  <Globe className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Global Reach</h3>
                  <p className="text-gray-600">From Silicon Valley startups to enterprises in India, we serve businesses worldwide with localized payment options.</p>
                </div>
              </div>

              <p className="text-lg text-gray-600 leading-relaxed">
                Today, URL Monitor Pro serves thousands of websites across the globe, from personal blogs to enterprise applications. We're proud to be the monitoring service that businesses trust to keep their digital presence running smoothly.
              </p>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                URL Monitor Pro by the numbers
              </h2>
              <p className="text-xl text-gray-600">
                Trusted by businesses worldwide
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center bg-white rounded-lg p-8 shadow-sm">
                <div className="text-4xl font-bold text-blue-600 mb-2">10,000+</div>
                <div className="text-gray-600">Websites Monitored</div>
              </div>

              <div className="text-center bg-white rounded-lg p-8 shadow-sm">
                <div className="text-4xl font-bold text-green-600 mb-2">99.97%</div>
                <div className="text-gray-600">Average Uptime</div>
              </div>

              <div className="text-center bg-white rounded-lg p-8 shadow-sm">
                <div className="text-4xl font-bold text-purple-600 mb-2">&lt;30s</div>
                <div className="text-gray-600">Alert Response Time</div>
              </div>

              <div className="text-center bg-white rounded-lg p-8 shadow-sm">
                <div className="text-4xl font-bold text-orange-600 mb-2">50+</div>
                <div className="text-gray-600">Countries Served</div>
              </div>
            </div>
          </div>
        </section>

        {/* Features We're Proud Of */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                What makes us different
              </h2>
              <p className="text-xl text-gray-600">
                We've built features that matter for real businesses
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Built for Global Businesses</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Dual Payment Systems</h4>
                      <p className="text-gray-600">Accept international cards via Stripe and Indian payments via UPI/Razorpay</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Multi-Channel Alerts</h4>
                      <p className="text-gray-600">Email, SMS, webhooks, and more - get notified the way you prefer</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Smart Rate Limiting</h4>
                      <p className="text-gray-600">Advanced email rate limiting prevents spam while ensuring critical alerts get through</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Real-time Dashboard</h4>
                      <p className="text-gray-600">Beautiful, responsive dashboard that works on all devices</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-8">
                <div className="text-center">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Professional Email System</h4>
                  <div className="bg-white rounded-lg p-6 shadow-sm">
                    <div className="text-left space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Email Service</span>
                        <span className="text-sm font-semibold text-green-600">✅ Resend</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Rate Limiting</span>
                        <span className="text-sm font-semibold text-blue-600">✅ Smart</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Templates</span>
                        <span className="text-sm font-semibold text-purple-600">✅ Professional</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Delivery</span>
                        <span className="text-sm font-semibold text-orange-600">✅ Instant</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-blue-600 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to join thousands of satisfied customers?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Start monitoring your websites today with our free plan. No credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/register"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              >
                Start Free Monitoring
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link 
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white text-lg font-semibold rounded-lg hover:bg-white hover:text-blue-600 transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <Link to="/" className="flex items-center justify-center mb-4">
                <Monitor className="w-8 h-8 text-blue-400 mr-3" />
                <span className="text-xl font-bold">URL Monitor Pro</span>
              </Link>
              <p className="text-gray-400">
                © 2024 URL Monitor Pro. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default AboutPage;